import type {
  MailReportsMailFiltersProps,
  MailReportsMailParams,
  MailReportsMailRow,
  MailReportsMailVariant,
} from '../types/pages'
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react'
import { Link } from 'react-router-dom'
import { Mail, Paperclip, RefreshCw } from 'lucide-react'
import { PageShell } from '@/components/layout/PageShell'
import { ListFilterDrawer } from '@/components/ui/ListFilterDrawer'
import { ListPaginationBar } from '@/components/ui/ListPaginationBar'
import { ListSearchToolbar } from '@/components/ui/ListSearchToolbar'
import { Tooltip } from '@/components/ui/Tooltip'
import { cn } from '@lib/utils/helpers'
import {
  SearchableSelect,
  type SearchableSelectOption,
} from '@/components/ui/SearchableSelect'
import { formatDateForAPI } from '@/features/invitee/utils/inviteeFormatters'
import { showApiError } from '@/lib/utils/notifications'
import {
  ReportDataTable,
  type ReportDataTableColumn,
} from '../components/ReportDataTable'
import { ReportDateRangePicker } from '../components/ReportDateRangePicker'
import { getGenericReportList, getLegacyMailLogList, getMailTemplateOptions } from '../services/reportsService'
import type { GenericReportListResponse } from '../types/api'
import {
  MAILGUN_LOG_DATE_PATTERN,
  MAIL_LOG_DATE_PATTERN,
} from '../constants/mailLogDates'
import { formatMailLogDate } from '../utils/mailLogDates'
import { dmyToYmd, ymdToDmy } from '../utils/reportDateParams'
import {
  clearGenericReportSession,
  normalizeSessionPatch,
  readGenericReportSession,
  writeGenericReportSession,
} from '../utils/genericReportSessionFilter'
import {
  INPUT_CLASS,
  LABEL_CLASS,
} from '../constants/configuredReportStyles'

function configForVariant(variant: MailReportsMailVariant) {
  if (variant === 'mailgun') {
    return {
      title: 'Mailgun Logs',
      testIdPrefix: 'mailgun-logs',
      sessionKey: 'mailgunlog_filter',
      apiPath: '/api/mailgun_logs',
    }
  }
  if (variant === 'imap') {
    return {
      title: 'IMAP Inbox',
      testIdPrefix: 'imap-logs',
      sessionKey: 'imapmaillog_filter',
      apiPath: '/api/mailtrace',
    }
  }
  return {
    title: 'Mail Logs (legacy)',
    testIdPrefix: 'mail-logs',
    sessionKey: 'maillog_filter',
    apiPath: '/api/mail_logs',
  }
}

function todayYmd() {
  return formatDateForAPI(new Date())
}

function defaultParams(variant: MailReportsMailVariant): MailReportsMailParams {
  return {
    page: 1,
    perPage: 15,
    to: '',
    fromDt: variant === 'mailgun' ? todayYmd() : '',
    toDt: variant === 'mailgun' ? todayYmd() : '',
    lr_form: '',
    event: '',
    fromTm: '',
    toTm: '',
    nonlinked: variant === 'imap' ? '1' : '',
  }
}

function initialParams(variant: MailReportsMailVariant): MailReportsMailParams {
  const config = configForVariant(variant)
  const defaults = defaultParams(variant)
  const session = readGenericReportSession(config.sessionKey)
  return session
    ? ({
        ...defaults,
        ...normalizeSessionPatch(session, { ...defaults }),
        ...(variant === 'imap' && session.search
          ? { to: String(session.search) }
          : {}),
      } as MailReportsMailParams)
    : defaults
}

function toDmy(value: string): string {
  if (!value) return ''
  return ymdToDmy(dmyToYmd(value) || value) || value
}

function buildMailParams(
  variant: MailReportsMailVariant,
  params: MailReportsMailParams,
): URLSearchParams {
  const qs = new URLSearchParams()
  qs.set('page', String(params.page))
  qs.set('perPage', String(params.perPage))
  if (params.to.trim()) qs.set('to', params.to.trim())
  if (params.lr_form.trim()) qs.set('lr_form', params.lr_form.trim())
  if (variant === 'legacy') qs.set('type', 'database')
  if (variant === 'imap') {
    if (params.to.trim()) {
      qs.delete('to')
      qs.set('search', params.to.trim())
    }
    if (params.nonlinked) qs.set('nonlinked', params.nonlinked)
  }
  if (variant === 'mailgun' && params.event.trim())
    qs.set('event', params.event.trim())

  const fromDt = toDmy(params.fromDt)
  const toDt = toDmy(params.toDt)
  if (fromDt)
    qs.set(
      'fromDt',
      (variant === 'mailgun' || variant === 'imap') && params.fromTm
        ? `${fromDt} ${params.fromTm}:00`
        : fromDt,
    )
  if (toDt)
    qs.set(
      'toDt',
      (variant === 'mailgun' || variant === 'imap') && params.toTm
        ? `${toDt} ${params.toTm}:00`
        : toDt,
    )
  return qs
}

function countActiveFilters(params: MailReportsMailParams, variant: MailReportsMailVariant) {
  const defaults = defaultParams(variant)
  const changed = (key: keyof MailReportsMailParams) =>
    String(params[key] ?? '') !== String(defaults[key] ?? '')
  let count = 0
  if (params.to.trim() && changed('to')) count++
  if (changed('fromDt') || changed('toDt')) count++
  if (params.lr_form.trim() && changed('lr_form')) count++
  if (variant === 'mailgun' && params.event.trim() && changed('event')) count++
  if (
    (variant === 'mailgun' || variant === 'imap') &&
    (changed('fromTm') || changed('toTm'))
  )
    count++
  if (variant === 'imap' && changed('nonlinked')) count++
  return count
}

function attachmentCount(value: unknown): number {
  if (typeof value === 'number') return value
  if (Array.isArray(value)) return value.length
  return 0
}

function rowLogId(row: MailReportsMailRow): string {
  const id = row.id ?? row.mail_id ?? row.mail_log_id
  return id == null ? '' : String(id)
}

function mailReadPath(variant: MailReportsMailVariant, row: MailReportsMailRow): string {
  const id = rowLogId(row)
  if (!id) return ''
  if (variant === 'mailgun') return `/reports/mailgun-logs/read/${encodeURIComponent(id)}`
  if (variant === 'imap') return `/imap-logs/read/${encodeURIComponent(id)}`
  return `/reports/mail-logs/read/${encodeURIComponent(id)}`
}

function MailReadLink({
  variant,
  row,
  children,
}: {
  variant: MailReportsMailVariant
  row: MailReportsMailRow
  children: ReactNode
}) {
  const path = mailReadPath(variant, row)
  if (!path) return <>{children}</>
  return (
    <Link to={path} className="text-[#007BFF] hover:underline">
      {children}
    </Link>
  )
}

function MailReadIconLink({
  variant,
  row,
}: {
  variant: MailReportsMailVariant
  row: MailReportsMailRow
}) {
  const path = mailReadPath(variant, row)
  const icon = <Mail className="h-4 w-4 text-amber-500" aria-hidden />
  if (!path) return icon

  return (
    <Link
      to={path}
      title="Read mail"
      aria-label="Read mail"
      className="inline-flex h-8 w-8 items-center justify-center rounded-lg text-amber-500 transition hover:bg-amber-50 hover:text-amber-600"
    >
      {icon}
    </Link>
  )
}

function MailRecipientCell({
  variant,
  row,
}: {
  variant: MailReportsMailVariant
  row: MailReportsMailRow
}) {
  const raw = String(row.recipient ?? row.fromName ?? '').trim()
  if (!raw) return <span className="text-slate-400">—</span>

  // Split on commas or semicolons, remove blanks
  const emails = raw
    .split(/[,;]+/)
    .map((s) => s.trim())
    .filter(Boolean)

  if (emails.length === 0) {
    return <span className="text-slate-400">—</span>
  }

  const firstEmail = emails[0]
  const remainingEmails = emails.slice(1)

  if (emails.length === 1) {
    return (
      <MailReadLink variant={variant} row={row}>
        <span className="truncate max-w-[260px] inline-block align-middle" title={firstEmail}>
          {firstEmail}
        </span>
      </MailReadLink>
    )
  }

  const tooltipContent = (
    <div className="py-0.5 min-w-[220px] max-w-[340px] text-xs text-left">
      <div className="text-[11px] font-bold text-amber-900 uppercase tracking-wider pb-1.5 mb-1.5 border-b border-amber-200/90 flex items-center justify-between">
        <span>All Recipients</span>
        <span className="text-[10px] text-amber-700 font-semibold bg-amber-100 px-1.5 py-0.5 rounded-full">
          {emails.length} total
        </span>
      </div>
      <div className="space-y-1 max-h-48 overflow-y-auto pr-1">
        {emails.map((email, idx) => (
          <div
            key={idx}
            className={cn(
              'truncate px-2 py-1 rounded-md text-xs transition-colors',
              idx === 0
                ? 'bg-amber-200/70 text-amber-950 font-semibold'
                : 'text-amber-950 hover:bg-amber-100 font-medium',
            )}
            title={email}
          >
            {email}
          </div>
        ))}
      </div>
    </div>
  )

  return (
    <div className="inline-flex items-center gap-1.5 max-w-full">
      <MailReadLink variant={variant} row={row}>
        <span className="truncate max-w-[170px] sm:max-w-[210px] inline-block align-middle">
          {firstEmail}
        </span>
      </MailReadLink>
      <Tooltip
        content={tooltipContent}
        side="bottom"
        interactive
        multiline
        className="!bg-amber-50 !border !border-amber-300/90 !shadow-xl !p-2.5 !rounded-xl !text-slate-800"
        arrowClassName="!bg-amber-50 !border-t !border-l !border-amber-300/90"
      >
        <span
          className="inline-flex cursor-pointer items-center rounded-md bg-blue-50 px-1.5 py-0.5 text-xs font-semibold text-[rgb(21,151,255)] ring-1 ring-inset ring-[rgb(21,151,255)]/30 hover:bg-blue-100 transition-colors shrink-0"
        >
          +{remainingEmails.length}
        </span>
      </Tooltip>
    </div>
  )
}

function MailFilters({
  variant,
  filters,
  isOpen,
  onClose,
  onApply,
  onReset,
}: MailReportsMailFiltersProps) {
  const [draft, setDraft] = useState<MailReportsMailParams>(filters)
  const [templateOptions, setTemplateOptions] = useState<
    SearchableSelectOption[]
  >([])

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- intentional: re-sync the filter draft to applied filters each time the drawer opens
    if (isOpen) setDraft(filters)
  }, [isOpen, filters])

  const loadTemplates = async (search: string) => {
    try {
      setTemplateOptions(await getMailTemplateOptions(search))
    } catch (err) {
      showApiError(err)
    }
  }

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- intentional: fetch template options when the drawer opens (data-load effect)
    if (isOpen && variant !== 'imap') void loadTemplates('')
  }, [isOpen, variant])

  const applyTimer = useRef<number | null>(null)

  const cancelPendingApply = () => {
    if (applyTimer.current !== null) {
      window.clearTimeout(applyTimer.current)
      applyTimer.current = null
    }
  }

  useEffect(() => cancelPendingApply, [])

  /** Filters apply as soon as they change; the time inputs debounce so partial entry doesn't refetch. */
  const patch = (partial: Partial<MailReportsMailParams>, debounce = false) => {
    const next = { ...draft, ...partial }
    setDraft(next)
    cancelPendingApply()
    if (debounce) {
      applyTimer.current = window.setTimeout(() => onApply(next), 500)
      return
    }
    onApply(next)
  }

  return (
    <ListFilterDrawer
      isOpen={isOpen}
      onClose={onClose}
      onReset={onReset}
      onDone={() => {
        cancelPendingApply()
        onApply(draft)
        onClose()
      }}
      testIdPrefix={configForVariant(variant).testIdPrefix}
    >
      <div className="space-y-4">
        <div className="space-y-2">
          <label className={LABEL_CLASS}>Date Range</label>
          <ReportDateRangePicker
            startDate={draft.fromDt}
            endDate={draft.toDt}
            onChange={(start, end) =>
              patch({
                fromDt: start ? formatDateForAPI(start) : '',
                toDt: end ? formatDateForAPI(end) : '',
              })
            }
          />
        </div>

        {variant !== 'imap' ? (
          <div className="space-y-2">
            <label className={LABEL_CLASS}>LR Form</label>
            <SearchableSelect
              options={templateOptions}
              value={draft.lr_form}
              onChange={(value) =>
                patch({
                  lr_form: Array.isArray(value) ? (value[0] ?? '') : value,
                })
              }
              onSearch={loadTemplates}
              onOpen={() => loadTemplates('')}
              placeholder="LR Form"
            />
          </div>
        ) : null}

        {variant === 'imap' ? (
          <div className="space-y-2">
            <label className={LABEL_CLASS}>Linked Status</label>
            <SearchableSelect
              searchable={false}
              options={[
                { value: '0', label: 'Linked' },
                { value: '1', label: 'Non Linked' },
              ]}
              value={draft.nonlinked}
              onChange={(val) => {
                const v = Array.isArray(val) ? val[0] : val
                patch({ nonlinked: v || '' })
              }}
              placeholder="All"
            />
          </div>
        ) : null}

        {variant === 'mailgun' ? (
          <>
            <div className="space-y-2">
              <label className={LABEL_CLASS}>Event</label>
              <SearchableSelect
                searchable={false}
                options={[
                  { value: 'delivered', label: 'Delivered' },
                  { value: 'permanant_failure', label: 'Permanent Failure' },
                ]}
                value={draft.event}
                onChange={(val) => {
                  const v = Array.isArray(val) ? val[0] : val
                  patch({ event: v || '' })
                }}
                placeholder="All Events"
              />
            </div>
          </>
        ) : null}

        {variant === 'mailgun' || variant === 'imap' ? (
          <>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <label className={LABEL_CLASS}>From Time</label>
                <input
                  type="time"
                  className={INPUT_CLASS}
                  value={draft.fromTm}
                  onChange={(e) => patch({ fromTm: e.target.value }, true)}
                />
              </div>
              <div className="space-y-2">
                <label className={LABEL_CLASS}>To Time</label>
                <input
                  type="time"
                  className={INPUT_CLASS}
                  value={draft.toTm}
                  onChange={(e) => patch({ toTm: e.target.value }, true)}
                />
              </div>
            </div>
          </>
        ) : null}
      </div>
    </ListFilterDrawer>
  )
}

function MailReportPage({ variant }: { variant: MailReportsMailVariant }) {
  const config = useMemo(() => configForVariant(variant), [variant])
  const [params, setParams] = useState<MailReportsMailParams>(() => initialParams(variant))
  const [searchDraft, setSearchDraft] = useState(() => params.to)
  const [rows, setRows] = useState<MailReportsMailRow[]>([])
  const [pageOptions, setPageOptions] = useState({
    current: 1,
    last: 1,
    total: 0,
  })
  const [loading, setLoading] = useState(false)
  const [filtersOpen, setFiltersOpen] = useState(false)

  useEffect(() => {
    setSearchDraft(params.to)
  }, [params.to])

  useEffect(() => {
    const timer = setTimeout(() => {
      const to = searchDraft.trim()
      if (to !== params.to.trim())
        setParams((prev) => ({ ...prev, to, page: 1 }))
    }, 500)
    return () => clearTimeout(timer)
  }, [params.to, searchDraft])

  const load = useCallback(
    async (next: MailReportsMailParams) => {
      setLoading(true)
      try {
        if (variant === 'legacy') {
          const res = await getLegacyMailLogList<MailReportsMailRow>(
            buildMailParams(variant, next),
          )
          const last = Math.max(
            1,
            Math.ceil(Number(res.total ?? 0) / next.perPage),
          )
          setRows(res.records ?? [])
          setPageOptions({
            current: next.page,
            last,
            total: Number(res.total ?? 0),
          })
        } else {
          const res: GenericReportListResponse<MailReportsMailRow> =
            await getGenericReportList(
              config.apiPath,
              buildMailParams(variant, next),
              variant === 'imap'
                ? 'Failed to load IMAP inbox.'
                : 'Failed to load mailgun logs.',
            )
          setRows(res.data ?? [])
          setPageOptions({
            current: Number(res.current ?? 1),
            last: Math.max(1, Number(res.last ?? 1)),
            total: Number(res.total ?? 0),
          })
        }
        writeGenericReportSession(config.sessionKey, {
          ...next,
          ...(variant === 'imap' ? { search: next.to } : {}),
        })
      } catch (err) {
        showApiError(err)
      } finally {
        setLoading(false)
      }
    },
    [config.apiPath, config.sessionKey, variant],
  )

  useEffect(() => {
    void load(params)
  }, [load, params])

  const resetFilters = () => {
    const defaults = defaultParams(variant)
    setSearchDraft('')
    clearGenericReportSession(config.sessionKey)
    setParams(defaults)
  }

  const columns = useMemo<ReportDataTableColumn<MailReportsMailRow, string>[]>(() => {
    if (variant === 'imap') {
      return [
        {
          id: 'icon',
          label: '',
          headerClassName: 'w-10',
          className: 'w-10',
          render: (row) => <MailReadIconLink variant={variant} row={row} />,
        },
        {
          id: 'from',
          label: 'From',
          headerClassName: 'w-[16%]',
          render: (row) => (
            <MailReadLink variant={variant} row={row}>
              {String(
                row.recipient ??
                  row.from_address ??
                  row.from ??
                  row.fromName ??
                  '—',
              )}
            </MailReadLink>
          ),
        },
        {
          id: 'to',
          label: 'To',
          headerClassName: 'w-[16%]',
          render: (row) => String(row.to ?? row.recipient ?? '—'),
        },
        {
          id: 'subject',
          label: 'Subject',
          className: 'font-medium text-slate-900',
          headerClassName: 'w-[38%]',
          render: (row) => (
            <MailReadLink variant={variant} row={row}>
              {String(row.subject ?? '—')}
            </MailReadLink>
          ),
        },
        {
          id: 'attachments',
          label: 'Att.',
          render: (row) =>
            attachmentCount(row.attachments_count ?? row.attachments) > 0 ? (
              <Paperclip className="h-3.5 w-3.5" aria-hidden />
            ) : (
              '—'
            ),
        },
        {
          id: 'created_at',
          label: 'Created',
          render: (row) =>
            formatMailLogDate(row.received_at ?? row.created_at, MAIL_LOG_DATE_PATTERN),
        },
      ]
    }
    if (variant === 'mailgun') {
      return [
        {
          id: 'icon',
          label: '',
          headerClassName: 'w-10',
          className: 'w-10',
          render: (row) => <MailReadIconLink variant={variant} row={row} />,
        },
        {
          id: 'recipient',
          label: 'To Address',
          headerClassName: 'w-[18%]',
          render: (row) => <MailRecipientCell variant={variant} row={row} />,
        },
        {
          id: 'subject',
          label: 'Subject',
          className: 'font-medium text-slate-900',
          headerClassName: 'w-[45%]',
          render: (row) => (
            <MailReadLink variant={variant} row={row}>
              <span className="inline-flex items-center gap-2">
              {String(row.subject ?? '—')}
              {attachmentCount(row.attachments) > 0 ? (
                <Paperclip className="h-3.5 w-3.5" aria-hidden />
              ) : null}
              </span>
            </MailReadLink>
          ),
        },
        {
          id: 'sent_at',
          label: 'Date',
          render: (row) => formatMailLogDate(row.sent_at, MAILGUN_LOG_DATE_PATTERN),
        },
        {
          id: 'delay',
          label: 'Delay',
          render: (row) => `${Number(row.delay ?? 0).toFixed(2)} Second`,
        },
        {
          id: 'event',
          label: 'Status',
          render: (row) => String(row.event ?? '—'),
        },
      ]
    }
    return [
      {
        id: 'icon',
        label: '',
        headerClassName: 'w-10',
        className: 'w-10',
        render: (row) => <MailReadIconLink variant={variant} row={row} />,
      },
      {
        id: 'recipient',
        label: 'To / From',
        headerClassName: 'w-[18%]',
        render: (row) => <MailRecipientCell variant={variant} row={row} />,
      },
      {
        id: 'subject',
        label: 'Subject',
        className: 'font-medium text-slate-900',
        headerClassName: 'w-[45%]',
        render: (row) => (
          <MailReadLink variant={variant} row={row}>
            {String(row.subject ?? '—')}
          </MailReadLink>
        ),
      },
      {
        id: 'attachments',
        label: 'Att.',
        render: (row) =>
          attachmentCount(row.attachments) > 0 ? (
            <span className="inline-flex items-center gap-1">
              <Paperclip className="h-3.5 w-3.5" aria-hidden />
              {attachmentCount(row.attachments)}
            </span>
          ) : (
            '—'
          ),
      },
      {
        id: 'created_at',
        label: 'Date',
        render: (row) => formatMailLogDate(row.created_at, MAIL_LOG_DATE_PATTERN),
      },
    ]
  }, [variant])

  return (
    <div data-testid={`${config.testIdPrefix}-page`}>
      <PageShell
        title={config.title}
        pageHeaderTestIdRoot={`${config.testIdPrefix}-page-header`}
        contentClassName="!space-y-6 w-full max-w-none min-w-0"
      >
        <ListSearchToolbar
          searchValue={searchDraft}
          searchPlaceholder={
            variant === 'imap' ? 'Search Inbox...' : 'Search Mail...'
          }
          onSearchChange={setSearchDraft}
          activeFilterCount={countActiveFilters(params, variant)}
          onOpenFilters={() => setFiltersOpen(true)}
          testIdPrefix={config.testIdPrefix}
          actions={
            variant === 'imap' || variant === 'mailgun' ? (
              <button
                type="button"
                onClick={() => void load(params)}
                className="inline-flex flex-1 items-center justify-center gap-2 rounded-xl border border-slate-200 bg-white px-5 py-2.5 text-sm font-semibold text-slate-700 shadow-sm transition-all hover:bg-slate-50 hover:shadow-md md:flex-none"
              >
                <RefreshCw className="h-[18px] w-[18px]" aria-hidden />
                Refresh
              </button>
            ) : null
          }
        />

        <MailFilters
          variant={variant}
          filters={params}
          isOpen={filtersOpen}
          onClose={() => setFiltersOpen(false)}
          onApply={(next) => setParams({ ...next, page: 1 })}
          onReset={resetFilters}
        />

        <div className="flex w-full min-w-0 flex-col overflow-hidden rounded-xl border border-slate-200/90 bg-white shadow-sm">
          <ListPaginationBar
            placement="header"
            currentPage={pageOptions.current}
            lastPage={pageOptions.last}
            totalRecords={pageOptions.total}
            perPage={params.perPage}
            onPageChange={(page) => setParams((p) => ({ ...p, page }))}
            onPerPageChange={(perPage) =>
              setParams((p) => ({ ...p, perPage, page: 1 }))
            }
            testIdPrefix={config.testIdPrefix}
            summaryLayout="angular"
          />
          <ReportDataTable
            rows={rows}
            columns={columns}
            loading={loading}
            tableClassName="w-full table-fixed"
            wrapperClassName="w-full min-w-0"
            testIdPrefix={config.testIdPrefix}
            rowKey={(row, index) =>
              `${row.id ?? row.mail_id ?? ''}-${row.received_at ?? row.created_at ?? ''}-${
                row.subject ?? ''
              }-${index}`
            }
          />
          <ListPaginationBar
            placement="footer"
            currentPage={pageOptions.current}
            lastPage={pageOptions.last}
            totalRecords={pageOptions.total}
            perPage={params.perPage}
            onPageChange={(page) => setParams((p) => ({ ...p, page }))}
            onPerPageChange={(perPage) =>
              setParams((p) => ({ ...p, perPage, page: 1 }))
            }
            testIdPrefix={config.testIdPrefix}
            summaryLayout="angular"
          />
        </div>
      </PageShell>
    </div>
  )
}

export function LegacyMailLogsPage() {
  return <MailReportPage variant="legacy" />
}

export function MailgunLogsPage() {
  return <MailReportPage variant="mailgun" />
}

export function ImapInboxPage() {
  return <MailReportPage variant="imap" />
}
